import React, { CSSProperties, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { MdClose } from 'react-icons/md';
import { Virtuoso } from 'react-virtuoso';

import Downshift, { ControllerStateAndHelpers, StateChangeOptions } from 'downshift';
import isEqual from 'fast-deep-equal';
import toString from 'lodash/toString';
import matchSorter from 'match-sorter';
import removeAccents from 'remove-accents';
import { DropdownOption, DropdownOptionGroup, DropdownValue } from 'types';

import { styled } from '../../theme';
import { Color, Size, Status } from '../../utility/common-props-helpers';
import FlexRow from '../FlexRow';
import BaseInput from '../Input';
import SvgIcon from '../SvgIcon';
import { checkIsEnoughSpaceAboveForMenu, createOptionsFromGranularity } from './utils';

/**
 * Consts
 */
const MULTISELECT_HOTKEY = 'shiftKey';

/**
 * Types
 */
export type DropdownInnerState<T = DropdownValue> = {
    isOpen: boolean;
    inputValue: string | null;
    visibleOptions: DropdownOption<T>[];
    topOptions: DropdownOption<T>[];
};

export type OptionGetters = Required<
    Pick<
        Props,
        | 'optionValueGetter'
        | 'optionLabelGetter'
        | 'optionDisabledGetter'
        | 'optionHiddenGetter'
        | 'optionSearchTermGetter'
    >
>;

export type SingleEventValue = {
    target: {
        name?: string;
        value: DropdownValue | null;
    };
};

export type ArrayEventValue = {
    target: {
        name?: string;
        value: DropdownValue[];
    };
};

type SortOptionsOrder = 'asc';

const isArrayEventValue = (
    ev: SingleEventValue | ArrayEventValue | undefined
): ev is ArrayEventValue => ev != null && Array.isArray(ev.target.value);

const isOptionGroup = (option): option is DropdownOptionGroup =>
    typeof option === 'object' && 'options' in option;

const defaultProps = {
    size: 'medium',
    color: 'tertiary',
    filterTopOption: () => true,
    optionValueGetter: (o) => o,
    optionLabelGetter: (o) => o,
    optionDisabledGetter: (o) => false,
    optionHiddenGetter: (o) => false,
};

export type Props<T = DropdownValue> = {
    className?: string;
    id?: string;
    dataTestid?: string;
    style?: CSSProperties;
    size?: Size;
    color?: Color;
    status?: Status;
    autoFocus?: boolean;
    disabled?: boolean;
    isOpen?: boolean;
    isSearchable?: boolean;
    isClearable?: boolean;
    name?: string;
    placeholder?: string;
    options: (DropdownOption | DropdownOptionGroup)[];
    optionsLabel?: string;
    topOptionsLabel?: string;
    multiselectLabel?: string;
    sortOptionsOrder?: SortOptionsOrder;
    valueMode: 'single' | 'singleArray' | 'multiArray';
    value: DropdownValue | DropdownValue[] | null;
    optionValueGetter?: (option: DropdownOption) => DropdownValue;
    optionLabelGetter?: (option: DropdownOption) => string;
    optionDisabledGetter?: (option: DropdownOption) => boolean;
    optionHiddenGetter?: (option: DropdownOption) => boolean;
    optionSearchTermGetter?: (option: DropdownOption) => string;
    renderMenuHeader?: (innerState: DropdownInnerState) => ReactNode;
    renderOption?: (option: DropdownOption, optionsGetters: OptionGetters) => ReactNode;
    renderTopOption?: (option: DropdownOption, optionsGetters: OptionGetters) => ReactNode;
    inputFieldContainerStyle?: React.CSSProperties;
    innerInputFieldStyle?: React.CSSProperties;
    selectedOptionStyle?: React.CSSProperties;
    filterTopOption?: (option: DropdownOption) => boolean;
    onInputChange?: (inputValue: string) => void;
    onChange?: (ev: SingleEventValue | ArrayEventValue, innerState: DropdownInnerState) => void;
    onSelect?: (ev: SingleEventValue | ArrayEventValue, innerState: DropdownInnerState) => void;
    onRemove?: (ev: ArrayEventValue, innerState: DropdownInnerState) => void;
    onMultiSelect?: (ev: ArrayEventValue, innerState: DropdownInnerState) => void;
    onClear?: (ev: SingleEventValue | ArrayEventValue, innerState: DropdownInnerState) => void;
    checkIsOptionVisible?: ({
        inputSearchTerm,
        option,
        optionSearchTerm,
    }: {
        inputSearchTerm: string;
        option: DropdownOption;
        optionSearchTerm: string;
    }) => void;
    /**
     * previously onAfterChange
     */
    onClose?: () => void;
    inputFieldStyle?: object;
    isSecondary?: boolean;
    label?: string;
};

type State = {
    topOptions: DropdownOption[];
    visibleOptions: DropdownOption[];
    menuPosition: 'bottom' | 'top';
    multiItemSelectionStartIndex: number | null;
};

/**
 * Utils
 */
const ITEM_HEIGHT = 49;
const MAX_HEIGHT = 300;

/**
 * Styles
 */
const RootContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    fontFamily: theme.typography.fontFamily.medium,
    fontWeight: theme.typography.fontWeight.base,
    fontSize: '1.6rem',
    outline: 'none',
}));

const InputContainer = styled('div')({
    position: 'relative',
});

const InputFieldContainer = styled(BaseInput, {
    shouldForwardProp: (prop) => !['status'].includes(prop),
})<
    Pick<
        Props,
        | 'id'
        | 'size'
        | 'status'
        | 'disabled'
        | 'color'
        | 'isSearchable'
        | 'valueMode'
        | 'inputFieldStyle'
        | 'isSecondary'
    >
>(
    {},
    ({
        theme,
        size,
        status,
        disabled,
        color,
        isSearchable,
        valueMode,
        inputFieldStyle,
        isSecondary,
    }) => ({
        outline: 'none',
        width: '100%',
        padding: isSecondary ? '3px 40px 3px 15px' : '5px 40px 5px 15px',
        cursor: 'text',
        color: isSecondary ? theme.color[color + '1'] : theme.color[color + 'Text'],
        border: isSecondary
            ? `2px solid ${theme.color[color + '1']}`
            : theme.border[`${color}`] || 'unset',
        backgroundColor: isSecondary ? '#fff' : theme.color[color + '1'],
        ...(valueMode === 'multiArray' && {
            height: 'auto',
        }),
        ...(!isSearchable && {
            cursor: 'pointer',
        }),
        ...(disabled && {
            opacity: 0.7,
            pointerEvents: 'none',
        }),
        ...(status && {
            border: '1px solid ' + theme.color[status],
        }),
        ...inputFieldStyle,
        ':hover': isSecondary
            ? {
                  backgroundColor: theme.color.base2,
                  color: theme.color.white,
                  button: { color: theme.color.white },
              }
            : {},
    })
);

const InputField = styled('input')<Pick<Props, never>>(
    {
        background: 'transparent',
        border: 'none',
        height: 40,
        padding: '0 0 0 0',
        font: 'inherit',
        cursor: 'inherit',
    },
    ({ theme }) => ({
        caretColor: theme.color.primary1,
        '::placeholder': {
            color: theme.color.gray40,
        },
    })
);

const SelectedOptionContainer = styled('div')<
    Pick<Props, 'size' | 'valueMode' | 'disabled'> & { isPlaceholder?: boolean }
>(
    {
        margin: '5px 10px 5px 0',
        padding: '5px 8px',
        minHeight: 30,
        display: 'inline-block',
    },
    ({ theme, size, valueMode, disabled, isPlaceholder }) => ({
        ...(valueMode === 'multiArray' && {
            backgroundColor: '#fff',
            borderRadius: theme.borderRadius.base,
        }),
        ...(isPlaceholder && {
            color: theme.color.gray40,
        }),
        ...(disabled && {
            color: theme.color.disabled,
        }),
    })
);

const SelectedOption = styled('div')<Pick<Props, 'size' | 'valueMode'>>(
    {
        // display: 'flex',
        // alignItems: 'center',
    },
    ({ theme, size, valueMode }) => ({
        width: '100%',
        // textDecoration: 'underline',
        ...(valueMode === 'multiArray' && {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
    })
);

const SelectedOptionRemoveButton = styled('button')<Pick<Props, 'size'>>(
    {
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        padding: '0 0',
        marginLeft: 4,
    },
    ({ theme, size }) => ({})
);

const StyledClearIcon = styled(MdClose)(({ theme }) => ({
    verticalAlign: 'top',
    height: 20,
    marginRight: -2,
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.red,
    },
    ':active': {
        color: theme.color.primary3,
    },
}));

const ActionButton = styled('button')<Pick<Props, 'disabled' | 'color' | 'isSecondary'>>(
    ({ theme, disabled, color, isSecondary }) => ({
        color: isSecondary ? theme.color.baseIcon : theme.color[color + 'Icon'],
        position: 'absolute',
        padding: 0,
        top: 0,
        right: 14,
        height: '100%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        ...(disabled && {
            opacity: 0.7,
            pointerEvents: 'none',
        }),
    })
);

const MenuContainer = styled('div')<{
    isOnTop: boolean;
}>(
    ({ theme, isOnTop }) => ({
        zIndex: isOnTop ? theme.zIndex.dropdownMenus : 1, // required to be on top of PageHeader in opened state
        position: 'absolute',
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
    }),
    ({ theme, isOnTop }) => ({
        marginTop: 0,
        background: theme.color.secondary1,
        boxShadow: theme.boxShadow.base1,
        ...(isOnTop
            ? {
                  bottom: '100%',
                  borderRadius: `${theme.borderRadius.base}px ${theme.borderRadius.base}px 0 0`,
              }
            : {
                  top: '100%',
                  borderRadius: `0 0 ${theme.borderRadius.base}px ${theme.borderRadius.base}px`,
              }),
    })
);

const TopOptionList = styled('ul')(({ theme }) => ({
    padding: 0,
}));

const TopOptionLabel = styled('div')(({ theme }) => ({
    margin: '0 30px 0 20px',
    paddingTop: 14,
    paddingBottom: 14,
    fontSize: '1.2rem',
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.color.gray40,
    textTransform: 'uppercase',
}));

const TopOptionFlexRow = styled(FlexRow)(({ theme }) => ({
    borderBottom: '1px solid rgba(162,163,164, 0.3)',
}));

const OptionListItem = styled('li')<{
    isSelected: boolean;
    isHighlighted: boolean;
    isMultiSelectionHighlighted: boolean;
    disabled: boolean;
}>(
    {
        cursor: 'pointer',
        display: 'block',
        padding: '13px 20px',
        minHeight: ITEM_HEIGHT + 'px',
        textTransform: 'none',
        whiteSpace: 'pre-wrap',
        wordWrap: 'normal',
    },
    ({ theme, isSelected, isHighlighted, isMultiSelectionHighlighted, disabled }) => ({
        color: theme.color.jetBlack,
        ...(isSelected && {
            fontWeight: theme.typography.fontWeight.medium,
            fontSize: 'inherit',
            color: theme.color.primary1,
        }),
        ...(isHighlighted && {
            background: '#e9eaea',
        }),
        ...(isMultiSelectionHighlighted && {
            background: '#e9eaea',
        }),
        ...(disabled && {
            color: theme.color.gray40,
            cursor: 'default',
        }),
    })
);

/**
 * Component
 */

/**
 @todo: disable search if there are no options
 @todo: dropdown empty value
 @todo: size: 'large' | 'base', needs to be implemented
 @todo: isSearchable: needs to be fixed for years dropdown
 @todo: closeMenuOnSelect: needs to be implemented
 @todo: required:  needs to be implemented
 **/

export class Dropdown extends React.PureComponent<Props, State> {
    static createOptionsFromGranularity = createOptionsFromGranularity;
    static defaultProps = defaultProps;

    listRef = React.createRef<typeof Virtuoso>();
    inputRef = React.createRef<HTMLInputElement>();
    menuRef = React.createRef<HTMLDivElement>();

    mounted = false;

    state: State = {
        ...this.getVisibleOptions(this.props.options),
        menuPosition: 'bottom',
        multiItemSelectionStartIndex: null,
    };

    constructor(props) {
        super(props);
        const { valueMode, value } = props;

        // check mode and value for errors
        if (value != null) {
            switch (valueMode) {
                case 'single':
                    if (Array.isArray(value)) {
                        throw Error("dropdown value shouldn't be an array in current valueMode");
                    }
                    break;
                case 'singleArray':
                case 'multiArray':
                    if (!Array.isArray(value)) {
                        throw Error('dropdown value should be an array in current valueMode');
                    }
                    break;

                default:
                    throw Error('dropdown has wrong valueMode');
            }
        }
    }

    componentDidMount(): void {
        this.mounted = true;
        window.addEventListener('scroll', this.handleScroll, true);
        this.updateMenuPosition();
    }

    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener('scroll', this.handleScroll);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { options, optionValueGetter } = this.props;

        if (
            !isEqual(
                this.getVisibleOptions(prevProps.options).visibleOptions.map(optionValueGetter),
                this.getVisibleOptions(options).visibleOptions.map(optionValueGetter)
            )
        ) {
            const searchValue = this.inputRef?.current?.value;
            if (searchValue) {
                this.updateVisibleOptions(searchValue);
            } else {
                this.resetVisibleOptions();
            }
        }

        this.updateMenuPosition();
    }

    isMulti = () => this.props.valueMode === 'multiArray';
    isSingleArray = () => this.props.valueMode === 'singleArray';

    isArrayValueMode = () => this.isMulti() || this.isSingleArray();

    handleScroll = () => this.updateMenuPosition();

    sortOptions(options) {
        const { optionSearchTermGetter = this.props.optionLabelGetter, sortOptionsOrder } =
            this.props;
        let sortingFunction;

        switch (sortOptionsOrder) {
            case 'asc':
            default:
                sortingFunction = (a, b) =>
                    optionSearchTermGetter(a).localeCompare(optionSearchTermGetter(b));
        }

        options.sort(sortingFunction);
    }

    // update menu position to be top or bottom
    updateMenuPosition = () => {
        if (!this.mounted) {
            return;
        }

        const node = ReactDOM.findDOMNode(this) as Element | null;
        if (!node) {
            return;
        }

        const rect = node.getBoundingClientRect();
        if (!rect) {
            return;
        }

        const menuPosition =
            rect.y + 350 > window.innerHeight &&
            checkIsEnoughSpaceAboveForMenu({
                triggerNode: node,
                triggerRect: rect,
                menuNode: this.menuRef.current,
            })
                ? 'top'
                : 'bottom';

        if (menuPosition !== this.state.menuPosition && this.mounted) {
            this.setState({ menuPosition });
        }
    };

    /**
     * Works only with visible items in the list menu
     */
    scrollToItem = (highlightedIndex) => {
        if (this.listRef.current != null) {
            this.listRef.current.scrollToItem(highlightedIndex);
        }
    };

    defaultRenderOption = (option: DropdownOption, optionGetters: OptionGetters) =>
        option == null ? '' : optionGetters.optionLabelGetter(option);

    resetVisibleOptions() {
        this.setState({
            ...this.getVisibleOptions(this.props.options),
        });
    }

    updateVisibleOptions(inputValue) {
        this.setState({
            ...this.getVisibleOptions(this.props.options, inputValue),
        });
    }

    flattenOptionsProp(options: Props['options']): DropdownOption[] {
        const { sortOptionsOrder } = this.props;
        const flattenOptions = options.reduce((result: DropdownOption[], option) => {
            if (isOptionGroup(option)) {
                return result.concat(option.options);
            }
            return result.concat(option);
        }, []);
        if (sortOptionsOrder) {
            this.sortOptions(flattenOptions);
        }

        return flattenOptions;
    }

    checkIsOptionVisible = (inputValue: string, option: DropdownOption) => {
        const { optionSearchTermGetter = this.props.optionLabelGetter } = this.props;
        const inputSearchTerm = removeAccents(toString(inputValue).toLowerCase());
        const optionSearchTerm = removeAccents(
            toString(optionSearchTermGetter(option)).toLowerCase()
        );

        const isVisible = this.props.checkIsOptionVisible
            ? this.props.checkIsOptionVisible({ inputSearchTerm, option, optionSearchTerm })
            : optionSearchTerm.includes(inputSearchTerm);
        return isVisible;
    };

    getVisibleOptions(
        options: Props['options'],
        inputValue?: string | null
    ): {
        visibleOptions: DropdownOption[];
        topOptions: DropdownOption[];
    } {
        const { optionSearchTermGetter = this.props.optionLabelGetter, sortOptionsOrder } =
            this.props;
        // returns initial options on mount or on reset
        if (!inputValue) {
            return {
                visibleOptions: this.flattenOptionsProp(options),
                topOptions: [],
            };
        }
        const checkIsOptionVisible = this.checkIsOptionVisible.bind(this, inputValue);

        const visibleTopOptions = [] as DropdownOption[];
        const visibleOptions = options.reduce((visibleOptions: DropdownOption[], optionOrGroup) => {
            if (isOptionGroup(optionOrGroup)) {
                const optionGroup = optionOrGroup;
                const visibleGroupOptions = optionGroup.options.filter(checkIsOptionVisible);
                const visibleTopOption = matchSorter(visibleGroupOptions, inputValue, {
                    keys: [
                        {
                            threshold: matchSorter.rankings.CONTAINS,
                            key: (option) => optionSearchTermGetter(option),
                        },
                    ],
                })[0];
                if (visibleTopOption) {
                    visibleTopOptions.push(visibleTopOption);
                }
                return visibleOptions.concat(visibleGroupOptions);
            }

            const option = optionOrGroup;
            if (checkIsOptionVisible(option)) {
                return visibleOptions.concat(option);
            }

            return visibleOptions;
        }, []);

        if (sortOptionsOrder) {
            this.sortOptions(visibleOptions);
        }

        const topOptions = visibleTopOptions.filter(this.props.filterTopOption);

        return {
            visibleOptions,
            topOptions,
        };
    }

    getInnerState = (downshift: ControllerStateAndHelpers<DropdownOption>): DropdownInnerState => {
        const { visibleOptions, topOptions } = this.state;
        return {
            isOpen: downshift.isOpen,
            inputValue: downshift.inputValue,
            visibleOptions: visibleOptions,
            topOptions: topOptions,
        };
    };

    handleValueToLabel = (downshiftValue: DropdownOption | DropdownOption[] | null): string => {
        const { optionLabelGetter } = this.props;

        if (downshiftValue == null) {
            return '';
        }

        if (Array.isArray(downshiftValue)) {
            return downshiftValue.map(optionLabelGetter).join(', ');
        }

        return optionLabelGetter(downshiftValue);
    };

    getChangeEvent = (
        selectedOption: DropdownOption | null,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ): SingleEventValue | ArrayEventValue | undefined => {
        const { valueMode, value, optionValueGetter } = this.props;

        const selectedOptionValue = selectedOption && optionValueGetter(selectedOption);

        let arrayValue: DropdownValue[];
        switch (valueMode) {
            case 'single':
                if (Array.isArray(value)) {
                    throw Error('wrong value type, expected primitive or null');
                }

                // check for no changes
                if (selectedOptionValue === value) {
                    return;
                }

                const ev: SingleEventValue = {
                    target: {
                        name: this.props.name,
                        value: selectedOptionValue,
                    },
                };
                return ev;

            case 'singleArray':
                if (!Array.isArray(value)) {
                    throw Error('wrong value type, expected array');
                }

                // check for no changes
                if (selectedOptionValue === value[0]) {
                    return;
                }

                if (selectedOptionValue == null) {
                    arrayValue = [];
                } else {
                    arrayValue = [selectedOptionValue];
                }
                break;

            case 'multiArray':
                if (!Array.isArray(value)) {
                    throw Error('wrong value type, expected array');
                }

                // check for no changes
                if (selectedOptionValue == null) {
                    arrayValue = [];
                } else if (value.includes(selectedOptionValue)) {
                    // remove existing value
                    arrayValue = value.filter((val) => val !== selectedOptionValue);
                } else {
                    // add new value
                    arrayValue = value.concat(selectedOptionValue);
                }
                break;

            default:
                throw Error('wrong valueMode');
        }

        const ev: ArrayEventValue = {
            target: {
                name: this.props.name,
                value: arrayValue,
            },
        };

        return ev;
    };

    handleItemSelect = (
        selectedOption: DropdownOption,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ) => {
        const { onChange, onSelect } = this.props;

        if (this.isMulti() && event?.[MULTISELECT_HOTKEY]) {
            return;
        }

        const ev = this.getChangeEvent(selectedOption, downshift);
        if (ev != null) {
            const innerState = this.getInnerState(downshift);
            onChange?.(ev, innerState);
            onSelect?.(ev, innerState);
        }
    };

    handleClearButtonClick = (downshift: ControllerStateAndHelpers<DropdownOption>) => {
        const { onChange, onClear } = this.props;

        const ev = this.getChangeEvent(null, downshift);

        if (ev != null) {
            const innerState = this.getInnerState(downshift);
            onChange?.(ev, innerState);
            onClear?.(ev, innerState);
        }
    };

    handleRemoveButtonClick = (
        selectedOption: DropdownOption,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ) => {
        const { onChange, onRemove } = this.props;

        const ev = this.getChangeEvent(selectedOption, downshift);

        if (isArrayEventValue(ev)) {
            const innerState = this.getInnerState(downshift);
            onChange?.(ev, innerState);
            onRemove?.(ev, innerState);
        }
    };

    getRemoveButtonProps = ({ onClick, item, ...props }: any = {}, downshift) => {
        return {
            onClick: (e) => {
                e.preventDefault();
                onClick?.(e);
                e.stopPropagation();
                this.handleRemoveButtonClick(item, downshift);
            },
            ...props,
        };
    };

    stateReducer = (
        state: ControllerStateAndHelpers<DropdownOption>,
        changes: StateChangeOptions<DropdownOption>
    ) => {
        // const { valueMode, value } = this.props;

        switch (changes.type) {
            // when selected an item with a keyboard or mouse
            case Downshift.stateChangeTypes.keyDownEnter:
            case Downshift.stateChangeTypes.clickItem:
                // preserve open menu
                return {
                    ...changes,
                    inputValue: '',
                    isOpen: state.isOpen,
                    highlightedIndex: state.highlightedIndex,
                };

            // when changed a value prop
            case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem:
                if (this.isMulti()) {
                    // in multi selection preserve
                    return {
                        ...changes,
                        inputValue: state.inputValue,
                    };
                } else if (this.isSingleArray()) {
                    // in single array selection preserve
                    return {
                        ...changes,
                        inputValue: state.inputValue,
                        isOpen: false,
                    };
                } else {
                    // in single selection close dropdown on value change
                    return {
                        ...changes,
                        isOpen: false,
                    };
                }
        }

        // when dropdown is opened
        if (changes.hasOwnProperty('isOpen') && changes.isOpen) {
        }

        // when dropdown is closed
        if (changes.hasOwnProperty('isOpen') && !changes.isOpen) {
            // in multi selection
            if (this.isArrayValueMode()) {
                return {
                    ...changes,
                    inputValue: undefined,
                };
            }
        }

        return changes;
    };

    handleStateChange = (
        changes: StateChangeOptions<DropdownOption>,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ) => {
        const { onClose } = this.props;

        // when user input changed
        if (changes.hasOwnProperty('inputValue')) {
            // this.setState({inputValue: changes.inputValue})
            // if (changes.inputValue) {
            this.updateVisibleOptions(changes.inputValue);
            // } else {
            //     this.resetVisibleOptions();
            // }
        }

        // when dropdown is opened
        if (changes.hasOwnProperty('isOpen') && changes.isOpen) {
            this.resetVisibleOptions();
        }

        // when dropdown is closed
        if (changes.hasOwnProperty('isOpen') && !changes.isOpen) {
            this.setState({ multiItemSelectionStartIndex: null });
            onClose?.();
        }

        // handle scrolling when highlightedIndex has changed
        // TODO: not working with current virtualization library, implement with a different library
        // if (changes.hasOwnProperty('highlightedIndex') && changes.highlightedIndex != null) {
        // this.scrollToItem(change.highlightedIndex);
        // }
    };

    handleFocus = (event, downshift: ControllerStateAndHelpers<DropdownOption>) => {
        downshift.openMenu();
        this.inputRef.current?.select();
    };

    handleInputValueChange = (inputValue) => {
        this.props.onInputChange?.(inputValue);
    };

    checkIsSelectedItemChanged = (prevItem, item) => {
        return !isEqual(prevItem, item);
    };

    render() {
        const { onChange, onSelect, options, value, ...restProps } = this.props;
        const { visibleOptions } = this.state;

        return (
            <Downshift
                itemToString={this.handleValueToLabel}
                itemCount={visibleOptions.length}
                onStateChange={this.handleStateChange}
                onSelect={this.handleItemSelect}
                onInputValueChange={this.handleInputValueChange}
                selectedItem={value}
                selectedItemChanged={this.checkIsSelectedItemChanged}
                stateReducer={this.stateReducer}
                initialInputValue=""
                {...restProps}
            >
                {this.renderDropdown}
            </Downshift>
        );
    }

    renderMenuHeader = (downshift): ReactNode => {
        const { renderMenuHeader } = this.props;
        const innerState = this.getInnerState(downshift);

        if (renderMenuHeader == null) {
            return null;
        }

        return renderMenuHeader(innerState);
    };

    renderDropdown = (downshift: ControllerStateAndHelpers<DropdownOption>) => {
        const {
            autoFocus,
            status,
            size,
            color,
            isClearable,
            isSearchable,
            placeholder,
            disabled,
            name,
            renderOption,
            renderTopOption,
            optionsLabel,
            topOptionsLabel,
            multiselectLabel,
            style,
            id,
            dataTestid,
            valueMode,
            optionValueGetter,
            optionLabelGetter,
            optionDisabledGetter,
            optionSearchTermGetter,
            optionHiddenGetter,
            inputFieldStyle,
            isSecondary,
            label,
            inputFieldContainerStyle,
            innerInputFieldStyle,
            selectedOptionStyle,
            className,
        } = this.props;
        let { visibleOptions: rawVisibleOptions, topOptions: rawTopOptions } = this.state;
        const flatOptions = this.flattenOptionsProp(this.props.options);

        const removeHiddenOptions = (option) => {
            return !optionHiddenGetter(option);
        };

        const visibleOptions = rawVisibleOptions.filter(removeHiddenOptions);
        const topOptions = rawTopOptions.filter(removeHiddenOptions);

        const optionGetters = {
            optionValueGetter,
            optionLabelGetter,
            optionDisabledGetter,
            optionSearchTermGetter: optionSearchTermGetter || optionLabelGetter,
        };

        const {
            getRootProps,
            getInputProps,
            getToggleButtonProps,
            getItemProps,
            isOpen,
            toggleMenu,
            selectedItem: downshiftValue,
            highlightedIndex,
            inputValue = '',
        } = downshift;

        // get selected options from options by matching value
        let selectedOptions;
        switch (valueMode) {
            case 'single':
                selectedOptions =
                    downshiftValue == null
                        ? []
                        : flatOptions.filter((o) => optionValueGetter(o) === downshiftValue);
                break;

            case 'singleArray':
            case 'multiArray':
                selectedOptions =
                    downshiftValue == null
                        ? []
                        : flatOptions.filter((o) => downshiftValue.includes(optionValueGetter(o)));
                break;

            default:
                break;
        }

        const isOptionSelected = (option) =>
            selectedOptions.find(
                (selectedOption) => optionValueGetter(selectedOption) === optionValueGetter(option)
            ) !== undefined;

        const isMenuOnTop = this.state.menuPosition === 'top';
        const isSelectedOptionRemovable =
            this.isMulti() && (isClearable || selectedOptions.length > 1);

        return (
            <RootContainer {...getRootProps()} style={style} tabIndex="0" className={className}>
                <InputContainer>
                    <InputFieldContainer
                        id={id}
                        data-testid={dataTestid}
                        as="div"
                        {...{
                            isSecondary,
                            size,
                            status,
                            disabled,
                            color,
                            isSearchable,
                            valueMode,
                            inputFieldStyle,
                            onClick: (ev) =>
                                isSearchable ? this.handleFocus(ev, downshift) : toggleMenu(ev),
                        }}
                        style={inputFieldContainerStyle}
                        innerInputStyle={innerInputFieldStyle}
                    >
                        {isSearchable ? (
                            <InputField
                                {...getInputProps({
                                    ref: this.inputRef,
                                    size: placeholder != null ? placeholder.length : 20,
                                    isOpen,
                                    autoFocus,
                                    disabled,
                                    placeholder,
                                    name,
                                    value:
                                        inputValue && inputValue.trimStart
                                            ? inputValue.trimStart()
                                            : inputValue || '',
                                    readOnly: !isSearchable,
                                })}
                            />
                        ) : !selectedOptions.length ? (
                            <SelectedOptionContainer isPlaceholder={!label}>
                                {label || placeholder}
                            </SelectedOptionContainer>
                        ) : (
                            ''
                        )}
                        {selectedOptions.map((option) => (
                            <SelectedOptionContainer
                                key={optionValueGetter(option)}
                                valueMode={valueMode}
                                disabled={optionDisabledGetter(option)}
                            >
                                <SelectedOption style={selectedOptionStyle}>
                                    {optionLabelGetter(option)}
                                    {isSelectedOptionRemovable && (
                                        <SelectedOptionRemoveButton
                                            {...this.getRemoveButtonProps(
                                                { item: option },
                                                downshift
                                            )}
                                        >
                                            <StyledClearIcon />
                                        </SelectedOptionRemoveButton>
                                    )}
                                </SelectedOption>
                            </SelectedOptionContainer>
                        ))}
                        {isClearable && selectedOptions.length > 0 ? (
                            <ActionButton
                                color={color}
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    this.handleClearButtonClick(downshift);
                                }}
                                aria-label="clear selection"
                                disabled={disabled}
                                isSecondary={isSecondary}
                            >
                                <StyledClearIcon />
                            </ActionButton>
                        ) : (
                            <ActionButton
                                {...getToggleButtonProps({
                                    disabled,
                                    color,
                                })}
                                style={{ pointerEvents: 'none' }}
                                isSecondary={isSecondary}
                            >
                                <SvgIcon
                                    alt="Down Icon"
                                    src={
                                        isOpen
                                            ? '/icons/ic-arrow-up.svg'
                                            : '/icons/ic-arrow-down.svg'
                                    }
                                    data-testid="dropdown-down-chevron"
                                />
                            </ActionButton>
                        )}
                    </InputFieldContainer>
                </InputContainer>
                <MenuContainer isOnTop={isMenuOnTop} innerRef={this.menuRef}>
                    {isOpen && inputValue && this.renderMenuHeader(downshift)}
                    {!isOpen || topOptions.length === 0 ? null : (
                        <TopOptionList>
                            {topOptionsLabel && (
                                <TopOptionFlexRow justify="space-between">
                                    <TopOptionLabel>{topOptionsLabel}</TopOptionLabel>
                                </TopOptionFlexRow>
                            )}
                            {topOptions
                                .map((topOption) => {
                                    const visibleOptionIndex = visibleOptions.findIndex(
                                        (vo) =>
                                            optionValueGetter(vo) === optionValueGetter(topOption)
                                    );
                                    return [topOption, visibleOptionIndex];
                                })
                                .map(([topOption, index]) => (
                                    <OptionListItem
                                        key={`top_${optionValueGetter(topOption)}`}
                                        style={{ padding: '12px 20px 0 40px' }}
                                        {...getItemProps({
                                            item: topOption,
                                            key: 'top_' + optionValueGetter(topOption),
                                            index,
                                            disabled: optionDisabledGetter(topOption),
                                            isSelected: isOptionSelected(topOption),
                                            isHighlighted: highlightedIndex === index,
                                        })}
                                    >
                                        {renderTopOption
                                            ? renderTopOption(topOption, optionGetters)
                                            : this.defaultRenderOption(topOption, optionGetters)}
                                    </OptionListItem>
                                ))}
                        </TopOptionList>
                    )}

                    {!isOpen ? null : visibleOptions.length === 0 ? (
                        <OptionListItem
                            {...{
                                disabled: true,
                            }}
                        >
                            {'No options'}
                        </OptionListItem>
                    ) : (
                        <>
                            {(optionsLabel || multiselectLabel) && (
                                <TopOptionFlexRow justify="space-between">
                                    {optionsLabel && (
                                        <TopOptionLabel>{optionsLabel}</TopOptionLabel>
                                    )}
                                    {multiselectLabel && (
                                        <TopOptionLabel>{multiselectLabel}</TopOptionLabel>
                                    )}
                                </TopOptionFlexRow>
                            )}
                            <Virtuoso
                                style={{
                                    height:
                                        visibleOptions.length * ITEM_HEIGHT > MAX_HEIGHT
                                            ? MAX_HEIGHT
                                            : visibleOptions.length * ITEM_HEIGHT,
                                }}
                                data={visibleOptions}
                                itemContent={(index, option) => {
                                    const { onMultiSelect, name } = this.props;
                                    const { multiItemSelectionStartIndex } = this.state;

                                    const isSelected = isOptionSelected(option);

                                    const handleMultiClick = (event) => {
                                        const handleKeyUp = (ev) => {
                                            if (!ev[MULTISELECT_HOTKEY]) {
                                                document.removeEventListener('keyup', handleKeyUp);
                                                this.setState({
                                                    multiItemSelectionStartIndex: null,
                                                });
                                            }
                                        };

                                        const hotkeyPressed = !!event[MULTISELECT_HOTKEY];

                                        if (!isSelected && !hotkeyPressed) {
                                            this.setState({
                                                multiItemSelectionStartIndex: index,
                                            });
                                        } else {
                                            this.setState({
                                                multiItemSelectionStartIndex: null,
                                            });
                                        }

                                        if (hotkeyPressed && multiItemSelectionStartIndex != null) {
                                            // add selected items from multiItemSelectionStartIndex to index
                                            const multiselectedOptions =
                                                multiItemSelectionStartIndex >= index
                                                    ? visibleOptions.slice(
                                                          index,
                                                          multiItemSelectionStartIndex + 1
                                                      )
                                                    : visibleOptions.slice(
                                                          multiItemSelectionStartIndex,
                                                          index + 1
                                                      );

                                            const unselectedOptions = multiselectedOptions.filter(
                                                (option) =>
                                                    !selectedOptions
                                                        .map(optionValueGetter)
                                                        .includes(optionValueGetter(option))
                                            );

                                            if (unselectedOptions.length) {
                                                const ev = {
                                                    target: {
                                                        name: name,
                                                        value: selectedOptions
                                                            .concat(unselectedOptions)
                                                            .map(optionValueGetter),
                                                    },
                                                };
                                                const innerState = this.getInnerState(downshift);
                                                onMultiSelect?.(ev, innerState);
                                            }

                                            this.setState({
                                                multiItemSelectionStartIndex: index,
                                            });
                                        }
                                    };

                                    return (
                                        <div
                                            {...{
                                                // ref,
                                                style,
                                                disabled: optionDisabledGetter(option),
                                            }}
                                        >
                                            <OptionListItem
                                                {...getItemProps({
                                                    item: option,
                                                    index,
                                                    disabled: optionDisabledGetter(option),
                                                    isSelected: isSelected,
                                                    isHighlighted: highlightedIndex === index,
                                                    isMultiSelectionHighlighted:
                                                        multiItemSelectionStartIndex === index,
                                                    onClick: this.isMulti()
                                                        ? handleMultiClick
                                                        : undefined,
                                                })}
                                            >
                                                {renderOption
                                                    ? renderOption(option, optionGetters)
                                                    : this.defaultRenderOption(
                                                          option,
                                                          optionGetters
                                                      )}
                                            </OptionListItem>
                                        </div>
                                    );
                                }}
                            />
                        </>
                    )}
                </MenuContainer>
            </RootContainer>
        );
    };
}

export default Dropdown;
